if (!customElements.get('cinematic-hero')) {
  customElements.define('cinematic-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = [...this.querySelectorAll('.cinematic-hero__slide')];
      if (!this.slides.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.index = 0;
      this.editorSelected = false;
      this.hovering = false;
      this.visible = true;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.dots = [...this.querySelectorAll('[data-hero-index]')];
      this.querySelector('.cinematic-hero__navigation')?.removeAttribute('hidden');
      this.dots.forEach((button, i) => button.addEventListener('click', () => this.show(i), options));
      this.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') { this.hovering = true; this.schedule(); } }, options);
      this.addEventListener('pointerleave', () => { this.hovering = false; this.schedule(); }, options);
      this.addEventListener('focusin', () => this.schedule(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
      this.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1));
      }, options);
      this.addEventListener('pointerdown', event => { this.suppressClick = false; if (event.pointerType === 'touch') this.touch = { x: event.clientX, y: event.clientY }; }, options);
      this.addEventListener('pointerup', event => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x, dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClick = true;
          this.show(this.index + (dx < 0 ? 1 : -1) * (getComputedStyle(this).direction === 'rtl' ? -1 : 1));
        }
      }, options);
      this.addEventListener('click', event => { if (this.suppressClick) { event.preventDefault(); event.stopImmediatePropagation(); this.suppressClick = false; } }, { ...options, capture: true });
      this.addEventListener('pointercancel', () => { this.touch = null; }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => { this.resetProgress(); this.schedule(); }, options);
      this.addEventListener('shopify:block:select', event => { const slide = event.target.closest('.cinematic-hero__slide'); if (slide) { this.editorSelected = true; this.show(this.slides.indexOf(slide)); } }, options);
      this.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; this.schedule(); }, options);
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); }, { threshold: .2 });
      this.observer.observe(this);
      this.setupHeader(options);
      this.show(0);
    }
    show(index) {
      const next = (index + this.slides.length) % this.slides.length;
      if (this.slides[this.index]?.contains(document.activeElement) && next !== this.index) this.dots[next]?.focus({ preventScroll: true });
      this.index = next;
      this.slides.forEach((slide, i) => { slide.classList.toggle('is-active', i === next); slide.inert = i !== next; slide.setAttribute('aria-hidden', String(i !== next)); });
      this.dots.forEach((button, i) => button.setAttribute('aria-current', String(i === next)));
      // Load the upcoming image before its fade begins, including after wraparound.
      this.slides[(next + 1) % this.slides.length].querySelector('img')?.setAttribute('loading', 'eager');
      this.resetProgress();
      this.schedule();
    }
    resetProgress() {
      this.progress?.cancel();
      this.progress = null;
      const fill = this.dots[this.index]?.querySelector('.cinematic-hero__fill');
      if (!fill) return;
      if (this.dataset.autoplay !== 'true' || this.motion.matches) { fill.style.transform = 'scaleX(1)'; return; }
      fill.style.removeProperty('transform');
      // One animation clock controls both the visible counter and slide changes.
      const progress = fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
        duration: Math.max(1, Number(this.dataset.interval) || 6000), fill: 'forwards', easing: 'linear'
      });
      progress.pause();
      this.progress = progress;
      progress.finished.then(() => {
        if (this.isConnected && this.progress === progress) this.show(this.index + 1);
      }, () => {});
    }
    schedule() {
      if (!this.progress || !this.isConnected) return;
      const readingSlide = this.slides[this.index]?.contains(document.activeElement);
      if (this.editorSelected || this.motion.matches || !this.visible || document.hidden || this.hovering || readingSlide) this.progress.pause();
      else this.progress.play();
    }
    setupHeader(options) {
      const section = this.closest('.shopify-section');
      const main = document.querySelector('#MainContent');
      const firstSection = section === main?.querySelector(':scope > .shopify-section') && document.body.classList.contains('template-index');
      this.header = firstSection && this.dataset.overlayHeader === 'true' ? document.querySelector('.header-wrapper--cinematic') : null;
      this.toggleAttribute('data-header-overlay', Boolean(this.header));
      this.style.setProperty('--hero-header-offset', '0px');
      this.style.setProperty('--hero-page-offset', '0px');
      if (!firstSection) return;
      const update = () => {
        if (this.header) {
          this.style.setProperty('--hero-header-offset', `${this.header.getBoundingClientRect().height}px`);
          const sectionHeader = this.header.closest('.section-header');
          this.header.toggleAttribute('data-over-hero', this.getBoundingClientRect().top >= -32 && !sectionHeader?.classList.contains('shopify-section-header-hidden'));
        }
        // Include the announcement bar (and a non-overlaid header) once, not twice.
        this.style.setProperty('--hero-page-offset', `${Math.max(0, this.getBoundingClientRect().top + window.scrollY)}px`);
      };
      this.headerObserver = new ResizeObserver(update);
      document.querySelectorAll('.section-header, .announcement-bar-section').forEach(element => this.headerObserver.observe(element));
      if (this.header) this.headerObserver.observe(this.header);
      window.addEventListener('resize', update, options);
      window.addEventListener('scroll', () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(update); }, { ...options, passive: true });
      update();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.progress?.cancel(); this.progress = null; cancelAnimationFrame(this.frame);
      this.observer?.disconnect(); this.headerObserver?.disconnect();
      this.header?.removeAttribute('data-over-hero');
    }
  });
}
